/**
 * Client-side game state for the vertical slice.
 *
 * This mirrors the shape the Worker/DurableObject API will return, so screens are written
 * against the real contract now and swapping the source later is a one-file change.
 * Resource regeneration is computed from timestamps exactly as PlayerDO will do it.
 */

import { create } from 'zustand'
import type { Cents, Dollars, Line } from '@ashport/shared'
import { cents, toDollars } from '@ashport/shared/formulas'
import { live, serverNow, useSession } from './session'
import { FIRST_ERA } from './eras'

// ---------------------------------------------------------------- primitives

export type HeatBand = 'calm' | 'watched' | 'dangerous' | 'critical' | 'rico'
export type Risk = 'low' | 'medium' | 'high' | 'extreme'
export type Rarity = 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary' | 'exceptional'
export type Rank = 'Associate' | 'Soldier' | 'Capo' | 'Underboss' | 'Boss'

export const heatBand = (h: number): HeatBand =>
  h >= 90 ? 'rico' : h >= 75 ? 'critical' : h >= 50 ? 'dangerous' : h >= 25 ? 'watched' : 'calm'

export const heatLabel: Record<HeatBand, string> = {
  calm: 'Quiet',
  watched: 'Watched',
  dangerous: 'Dangerous',
  critical: 'Critical',
  rico: 'RICO Watch',
}

export const heatNote: Record<HeatBand, string> = {
  calm: 'Nobody is looking at you.',
  watched: 'A patrol car took your plate.',
  dangerous: 'Risk of arrest high.',
  critical: 'They have your name on a board.',
  rico: 'Federal task force is building a case.',
}

// ------------------------------------------------------------------ formatting

/**
 * MONEY IS CENTS. Every amount on the wire is an integer number of cents, because a game whose
 * whole economy is a dirty/clean split and a laundering haircut cannot afford a float, and
 * because `Number.MAX_SAFE_INTEGER` in cents is still ninety trillion dollars.
 *
 * These two take cents and are what every server-backed screen uses. `moneyD`/`exactD` below
 * take dollars and exist only for screens still running on mock data; each one disappears as
 * its screen gets wired.
 */
export const money = (c: Cents): string => moneyD(toDollars(c))
export const exact = (c: Cents): string =>
  `$${Math.round(toDollars(c)).toLocaleString('en-US')}`

/**
 * DOLLARS in. Deprecated the moment it was written.
 *
 * These exist only for the screens still running on mock data, and each one disappears as its
 * screen is wired to the server. When the last of them is gone, DELETE THESE TWO FUNCTIONS in
 * the same change. A dollars-taking money formatter left in a codebase where nothing calls it is
 * a loaded gun on the table.
 *
 * Still calling them, as of Phase 6. The count only ever goes DOWN:
 *   Contracts.tsx, Hitlist.tsx, Ledger.tsx,
 *   Scanner.tsx, Territory.tsx, home/Home.tsx
 *
 * Market.tsx came off this list in Phase 5, Families.tsx in Phase 6. Seven screens left.
 *
 * @deprecated Use `money`/`exact`, which take Cents, once the screen is server-wired.
 */
export const moneyD = (n: Dollars | number): string => {
  const a = Math.abs(n)
  if (a >= 1e9) return `$${(n / 1e9).toFixed(2)}B`
  if (a >= 1e6) return `$${(n / 1e6).toFixed(2)}M`
  if (a >= 1e4) return `$${Math.round(n / 1e3)}K`
  return `$${Math.round(n).toLocaleString('en-US')}`
}

/** @deprecated See `moneyD`. */
export const exactD = (n: Dollars | number): string =>
  `$${Math.round(n).toLocaleString('en-US')}`

export const clock = (s: number): string => {
  if (s < 0) s = 0
  const d = Math.floor(s / 86400)
  const h = Math.floor((s % 86400) / 3600)
  const m = Math.floor((s % 3600) / 60)
  const sec = Math.floor(s % 60)
  const p = (x: number) => String(x).padStart(2, '0')
  return d > 0 ? `${d}d ${p(h)}h ${p(m)}m` : `${p(h)}:${p(m)}:${p(sec)}`
}

export const short = (s: number): string => {
  const m = Math.floor(s / 60)
  return m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}:${String(Math.floor(s % 60)).padStart(2, '0')}`
}

// ------------------------------------------------------------------ districts

export interface District {
  id: string
  name: string
  short: string
  /** Ownership drives colour + pattern + marker, never colour alone. */
  control: 'you' | 'allied' | 'contested' | 'enemy' | 'neutral'
  /** Normalised polygon on the 2048x2048 city master, as percentages. */
  poly: [number, number][]
  /** Where the label sits, and where the map camera focuses. */
  at: [number, number]
  blocks: number
  income: number
  heat: number
  tone: string
  blurb: string
}

/**
 * MAP GEOMETRY, AND NOTHING ELSE. Twelve polygons and twelve label anchors.
 *
 * Traced by hand against `art/city/master-neutral.png` using a 5 percent coordinate grid, so
 * every polygon sits on the landmass it names: the foundry stacks, the church spire, the domed
 * courthouse, the marina slips and the container stacks all fall inside the right district.
 * Percentages of the square master.
 *
 * **THIS TABLE USED TO CARRY THE WHOLE DISTRICT.** Name, short name, blurb, control, blocks,
 * income, Heat and tint, twelve rows of them, all typed into a screen. Four things were wrong
 * with that and only the first is obvious:
 *
 *   1. Twelve canon district names lived in TypeScript, where nothing could check them against
 *      `content/districts.json`, which authors all twelve with the prose the writer wrote.
 *   2. **A brand new player was shown owning three districts.** `control: 'you'` on Little
 *      Sicily, Saint Brigid and Dockside, labelled "Marchetti Crew", against GAME-DESIGN:1574's
 *      "The city opens neutral" and against a server that has never sent a control value.
 *   3. The incomes exceeded the per-block ceiling `formulas.ts` allows, on five of the twelve.
 *      Verity Hill read 4 blocks and $402,000, which is 1.93x the maximum; the twelve summed to
 *      $2,211,000 against a ruled $1,270,000 for the entire map at 5,000 DAU.
 *   4. Heat is a PLAYER resource here and a DISTRICT resource in the design. Twelve district
 *      Heat values sat beside a real player Heat bar meaning something different.
 *
 * Geometry is the one thing about a district the server has no reason to know, so it is the one
 * thing left. Everything else comes from `/api/boot`, which reads `content/districts.json`.
 */
const GEOMETRY: Record<string, { poly: [number, number][]; at: [number, number] }> = {
  bell_foundry: { poly: [[14, 4], [30, 2], [46, 2], [53, 9], [51, 19], [32, 24], [14, 20], [9, 11]], at: [31, 12] },
  north_quay: { poly: [[55, 8], [70, 8], [78, 10], [77, 27], [66, 28], [55, 24]], at: [66, 18] },
  slaughterhouse_flats: { poly: [[78, 10], [90, 13], [97, 19], [96, 29], [84, 30], [78, 28]], at: [87, 20] },
  ironbound: { poly: [[3, 21], [14, 20], [32, 24], [36, 30], [30, 35], [8, 34], [1, 27]], at: [18, 28] },
  verity_hill: { poly: [[56, 28], [80, 29], [93, 33], [92, 47], [70, 51], [57, 47]], at: [74, 39] },
  little_sicily: { poly: [[6, 34], [30, 35], [48, 33], [49, 47], [28, 52], [7, 49]], at: [27, 42] },
  harbor_ward: { poly: [[57, 50], [80, 52], [95, 51], [94, 63], [73, 67], [58, 63]], at: [76, 58] },
  saint_brigid: { poly: [[7, 49], [28, 52], [45, 51], [44, 63], [22, 67], [4, 62]], at: [24, 57] },
  cinder_park: { poly: [[30, 57], [50, 54], [58, 62], [56, 73], [40, 78], [28, 70]], at: [43, 66] },
  dockside: { poly: [[74, 69], [90, 72], [97, 78], [92, 88], [80, 90], [70, 80]], at: [84, 79] },
  kepler_row: { poly: [[46, 78], [60, 74], [71, 80], [68, 93], [52, 95], [42, 87]], at: [57, 85] },
  south_docks: { poly: [[5, 67], [22, 67], [31, 76], [29, 90], [12, 92], [3, 80]], at: [17, 79] },
}

/** The order the map draws them in, which is the order they were traced. */
const GEOMETRY_ORDER = Object.keys(GEOMETRY)

/**
 * The twelve districts, joined from the server's content to the geometry above.
 *
 * Before boot lands there is nothing to join, and the honest answer to "which districts are
 * there" before the server has said so is an empty map rather than twelve invented ones. It is
 * one frame.
 */
export function districts(): District[] {
  const boot = useSession.getState().boot
  if (!boot) return []
  const byId = new Map(boot.districts.map((d) => [d.id as string, d]))
  return GEOMETRY_ORDER.flatMap((id) => {
    const d = byId.get(id)
    const g = GEOMETRY[id]
    if (!d || !g) return []
    return [
      {
        id,
        name: d.name,
        short: d.short,
        control: d.control,
        poly: g.poly,
        at: g.at,
        blocks: d.blocks,
        income: d.income,
        heat: d.heat,
        tone: controlTint[d.control],
        blurb: d.blurb,
      },
    ]
  })
}

/**
 * A district's display name, from the server or not at all.
 *
 * There is no local fallback table any more and that is deliberate. The fallback WAS the defect:
 * twelve canon names typed into this file, which nothing could check against
 * `content/districts.json`, and which would silently take over any time boot had not landed.
 * Before boot the honest answer is the id, which is ugly on screen for one frame and cannot be
 * mistaken for canon.
 */
export function districtName(id: string): string {
  return useSession.getState().boot?.districts.find((d) => d.id === id)?.name ?? id
}

export const controlTint: Record<District['control'], string> = {
  you: '#55b56a',
  allied: '#4aa8f0',
  contested: '#d6a84b',
  enemy: '#e53b2f',
  neutral: '#6d7a83',
}

/**
 * What a control state is CALLED, with the one that names a family taking the name as an argument.
 *
 * `you` used to read "Marchetti Crew" as a constant, so every account in the game was told the
 * districts it did not own belonged to a family it was not in. The player's own family name is on
 * `me.familyName` and is empty until they join one, which is the ordinary case for a new account
 * and is why this is a function rather than a table.
 */
export function controlLabel(control: District['control'], familyName?: string): string {
  if (control === 'you') return familyName && familyName !== '' ? familyName : 'Yours'
  return { allied: 'Allied Family', contested: 'Contested', enemy: 'Enemy Family', neutral: 'Unclaimed' }[
    control
  ]
}

/** Pattern id per control state, so ownership survives grayscale and colour-blindness. */
export const controlPattern: Record<District['control'], string> = {
  you: 'pat-hatch',
  allied: 'pat-dots',
  contested: 'pat-cross',
  enemy: 'pat-hazard',
  neutral: 'pat-stipple',
}

// -------------------------------------------------------------------- player

export interface Player {
  name: string
  handle: string
  /** The player's OWN portrait, off `me.portraitId`. Not a canon character's. */
  portraitId: string
  level: number
  xp: number
  xpNext: number
  rank: Rank
  nextRank: Rank
  /** PHASE 24. '' until the Line is drawn. */
  line: Line
  energy: number
  energyMax: number
  energyAt: number
  nerve: number
  nerveMax: number
  nerveAt: number
  /** CENTS. Branded, so a dollars value cannot reach a formatter expecting this. */
  dirty: Cents
  clean: Cents
  heat: number
  legend: number
  family: string
  sponsor: string
}

// ---------------------------------------------------------------------- feed

export interface FeedItem {
  id: string
  kind: 'news' | 'global'
  text: string
}

/*
 * `FEED` was here: six hand-written city headlines, three "news" and three "global", rotated
 * every seven seconds in the bottom ticker so the world would "read as running even when the
 * player is idle".
 *
 * Every one of the six was invented, and three of them stated things about the world that are
 * false: an Era that is not running, a family under a name canon does not use, and a bounty
 * collected by a person who does not exist against one who does. A rolling feed of fabricated
 * events beside a live Beat clock is the most convincing lie on the screen, because motion is
 * what makes a reader stop checking.
 *
 * The real feed is the Scanner. `content/flavour/scanner.json` holds forty authored dispatch
 * lines, `server/src/jobs/beat-materialise.ts` already renders them with the `{block}` token
 * substituted from the 24 authored landmark names, and PUBLISHING them to a client feed is
 * Phase 9. Until then the ticker carries the Beat, which is live, true, and the one thing in
 * the city that genuinely moves on its own.
 */

// ------------------------------------------------------------------- store

interface GameState {
  player: Player
  beat: { district: string; payout: number; heatMult: number; endsAt: number }
  era: { n: number; name: string; week: number; of: number; endsAt: number }
  now: number
  selected: string | null
  tick: () => void
  select: (id: string | null) => void
  spendHeat: (n: number) => void
}

const t0 = Date.now()

/**
 * The bridge from server truth to the shape every screen already reads.
 *
 * Resource bars are settled LOCALLY from `(value, at, periodMs)` on every tick, using the same
 * `settle()` and `decay()` the Durable Object uses. That is not optimistic UI and it is not a
 * prediction: it is the identical pure function over identical inputs, which is why the bar and
 * the server never disagree. Everything else here is copied, not computed.
 */
function fromServer(fallback: Player, now: number): Partial<GameState> {
  const s = useSession.getState()
  if (!s.me) return { now }
  const l = live(s.me, now)
  const beat = s.beatNow(now)
  const player: Player = {
    ...fallback,
    name: s.me.displayName,
    handle: s.me.handle,
    portraitId: s.me.portraitId,
    level: s.me.level,
    xp: s.me.xp,
    xpNext: s.me.xpNext,
    rank: s.me.rank,
    nextRank: s.me.nextRank ?? s.me.rank,
    line: s.me.line,
    energy: l.energy,
    energyMax: l.energyMax,
    energyAt: l.energyAt,
    nerve: l.nerve,
    nerveMax: l.nerveMax,
    nerveAt: l.nerveAt,
    dirty: s.me.dirty,
    clean: s.me.clean,
    heat: l.heat,
    legend: s.me.legend,
    family: s.me.familyName ?? 'No family',
    sponsor: s.me.sponsorName ?? 'No sponsor of record',
  }
  return {
    now,
    player,
    ...(beat
      ? { beat: { district: beat.district, payout: beat.payout, heatMult: beat.heatMult, endsAt: beat.endsAt } }
      : {}),
    ...(s.boot
      ? {
          era: {
            n: s.boot.era.n,
            name: s.boot.era.name,
            week: s.boot.era.week,
            of: s.boot.era.of,
            endsAt: s.boot.era.endsAt,
          },
        }
      : {}),
  }
}

export const useGame = create<GameState>((set, get) => ({
  /*
   * THE SEED IS EMPTY, and that is the whole design of it.
   *
   * `fromServer` replaces this entire slice on the first tick after boot, so it is visible for
   * one frame. It used to be a level 47 Capo called Salvatore "Sallie" Marchetti holding
   * $2.7 million dirty, Heat 78 and 12,540 Legend, with a named family and a named sponsor: a
   * complete fictional character, rendered for a fraction of a second to every new account
   * before their own name arrived. A zeroed seed is one frame of an empty bar, which is what an
   * account that has not loaded actually looks like.
   */
  player: {
    name: '',
    handle: '',
    portraitId: '',
    level: 1,
    xp: 0,
    xpNext: 0,
    rank: 'Associate',
    nextRank: 'Soldier',
    line: '',
    energy: 0,
    energyMax: 0,
    energyAt: t0,
    nerve: 0,
    nerveMax: 0,
    nerveAt: t0,
    dirty: cents(0),
    clean: cents(0),
    heat: 0,
    legend: 0,
    family: '',
    sponsor: '',
  },
  beat: { district: 'ironbound', payout: 0.35, heatMult: 2, endsAt: t0 + 51_753_000 },
  /*
   * The seed Era, from `content/eras.json` rather than from a pair of literals.
   *
   * It read "Era 3, The Harbor Accord" against a server running Era 1, which is one frame of a
   * wrong Era name on every cold load and the same class of error as the Ledger's transposed
   * record table. `fromServer` replaces the whole slice on the first tick after boot; until then
   * the first Era is the honest answer, because a client with no boot has not been told which
   * Era it is in and Era 1 is the one this game opens with.
   */
  era: { n: FIRST_ERA.n, name: FIRST_ERA.name, week: 1, of: 12, endsAt: t0 + 570_000_000 },
  now: t0,
  selected: null,

  // One clock for the whole app, and it is the SERVER'S. Deriving a countdown from the player's
  // own watch means a machine three minutes fast shows a bar filling early and then snapping
  // back, which reads as the game cheating.
  tick: () => set((s) => fromServer(s.player, serverNow())),
  select: (id) => set({ selected: id === get().selected ? null : id }),
  spendHeat: (n) =>
    set((s) => ({ player: { ...s.player, heat: Math.max(0, Math.min(100, s.player.heat + n)) } })),
}))

/** Seconds until the next Energy tick, matching the 4-minute regen cadence. */
export const nextTick = (at: number, now: number, periodMs: number) =>
  Math.ceil((periodMs - ((now - at) % periodMs)) / 1000)
